import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';
import { useBranch } from 'contexts/BranchContext';

export default function MemberRoster() {
  const { selectedBranchId } = useBranch();
  const [members, setMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTrainer, setFilterTrainer] = useState('all');
  const [assigningId, setAssigningId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 2000 };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const trainerParams = { role: 'trainer' };
      if (selectedBranchId) trainerParams.branch_id = selectedBranchId;
      const [membersRes, trainersRes] = await Promise.allSettled([
        api.get('/members', { params }),
        api.get('/staff', { params: trainerParams }),
      ]);
      if (membersRes.status === 'fulfilled') {
        setMembers(membersRes.value.data || []);
      } else {
        setError(membersRes.reason?.message);
      }
      if (trainersRes.status === 'fulfilled') {
        setTrainers(trainersRes.value.data || []);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssignTrainer = async (memberId, trainerId) => {
    setAssigningId(memberId);
    try {
      await api.put(`/members/${memberId}`, { trainer_id: trainerId || null });
      setMembers(prev => prev?.map(m => m?.id !== memberId ? m : { ...m, trainer_id: trainerId || null }));
    } catch (err) {
      setError(err?.message);
      setTimeout(() => setError(null), 4000);
    } finally {
      setAssigningId(null);
    }
  };

  const filtered = members?.filter((m) => {
    const fullName = `${m?.first_name} ${m?.last_name}`;
    const trainerName = m?.trainer_name || '';
    const matchSearch = fullName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      trainerName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = filterStatus === 'all' || m?.member_status === filterStatus;
    const matchTrainer = filterTrainer === 'all' || m?.trainer_id === filterTrainer;
    return matchSearch && matchStatus && matchTrainer;
  });

  const getActivePackage = (m) => m?.package_name || 'No Package';

  const statusColors = {
    active: 'bg-[var(--color-success)]/10 text-[var(--color-success)]',
    inactive: 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]',
    suspended: 'bg-[var(--color-error)]/10 text-[var(--color-error)]',
    trial: 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]',
  };

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Member Roster</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              placeholder="Search members..."
              className="pl-8 pr-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors w-44"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="trial">Trial</option>
          </select>
          <select
            value={filterTrainer}
            onChange={(e) => setFilterTrainer(e?.target?.value)}
            className="px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
          >
            <option value="all">All Trainers</option>
            {trainers?.map(t => (
              <option key={t?.id} value={t?.id}>{t?.full_name}</option>
            ))}
          </select>
        </div>
      </div>
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
        </div>
      )}
      {loading ? (
        <div className="flex flex-col gap-3">
          {[1, 2, 3, 4]?.map(i => (
            <div key={i} className="h-14 bg-[var(--color-muted)] rounded animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                {['Member', 'Status', 'Package', 'Trainer', 'Workout', 'Diet', 'Assign Trainer']?.map((h) => (
                  <th key={h} className="text-left py-2.5 px-3 text-xs font-semibold text-[var(--color-muted-foreground)] font-[var(--font-caption)] uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-10 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
                      <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No members found</p>
                    </div>
                  </td>
                </tr>
              ) : filtered?.map((m) => (
                <tr key={m?.id} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-muted)]/30 transition-colors">
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-[var(--color-primary)] font-[var(--font-caption)]">
                          {`${m?.first_name?.[0] || ''}${m?.last_name?.[0] || ''}`?.toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)] whitespace-nowrap">{m?.first_name} {m?.last_name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-[var(--font-caption)] ${statusColors?.[m?.member_status] || statusColors?.inactive}`}>
                      {m?.member_status}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-secondary)]/10 text-[var(--color-secondary)] font-medium font-[var(--font-caption)]">
                      {getActivePackage(m)}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                    {m?.trainer_name || <span className="text-[var(--color-muted-foreground)] italic">Unassigned</span>}
                  </td>
                  <td className="py-3 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-[var(--font-caption)] ${
                      m?.workout_plan_id ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'
                    }`}>
                      {m?.workout_plan_id ? 'Assigned' : 'None'}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-[var(--font-caption)] ${
                      m?.diet_plan_id ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'
                    }`}>
                      {m?.diet_plan_id ? 'Assigned' : 'None'}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    <select
                      value={m?.trainer_id || ''}
                      onChange={(e) => handleAssignTrainer(m?.id, e?.target?.value)}
                      disabled={assigningId === m?.id}
                      className="px-2 py-1.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors disabled:opacity-50"
                    >
                      <option value="">Unassigned</option>
                      {trainers?.map(t => (
                        <option key={t?.id} value={t?.id}>{t?.full_name}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
